import * as React from "react"
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "./accordion"
import { cn } from "@/lib/class-control"

interface AccordionItemData {
  id: string
  title: React.ReactNode | string
  content: React.ReactNode
  disabled?: boolean
}

interface SimpleAccordionProps {
  items: AccordionItemData[]
  type?: "single" | "multiple"
  collapsible?: boolean
  defaultValue?: string | undefined
  className?: string
  itemClassName?: string
  triggerClassName?: string
  contentClassName?: string
}

export const SimpleAccordion: React.FC<SimpleAccordionProps> = ({
  items,
  type = "single",
  collapsible = true,
  defaultValue = undefined,
  className,
  itemClassName,
  triggerClassName,
  contentClassName,
}) => {
  const finalDefaultValue = defaultValue || (items.length > 0 ? items[0].id : undefined)

  return (
    <Accordion
      defaultValue={finalDefaultValue}
      type={"single"}
      collapsible={collapsible}
      className={cn("w-full", className)}
    >
      {items.map((item) => (
        <AccordionItem
          key={item.id}
          value={item.id}
          disabled={item.disabled}
          className={itemClassName}
        >
          <AccordionTrigger className={triggerClassName}>
            {item.title}
          </AccordionTrigger>

          <AccordionContent className={contentClassName}>
            {item.content}
          </AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  )
}
// eslint-disable-next-line react-refresh/only-export-components

export const useAccordionItems = () => {
  const [items, setItems] = React.useState<AccordionItemData[]>([])

  const addItem = React.useCallback((item: AccordionItemData) => {
    setItems(prev => [...prev, item])
  }, [])

  const removeItem = React.useCallback((id: string) => {
    setItems(prev => prev.filter(item => item.id !== id))
  }, [])

  const updateItem = React.useCallback((id: string, updates: Partial<AccordionItemData>) => {
    setItems(prev => prev.map(item =>
      item.id === id ? { ...item, ...updates } : item
    ))
  }, [])

  const clearItems = React.useCallback(() => {
    setItems([])
  }, [])

  return {
    items,
    addItem,
    removeItem,
    updateItem,
    clearItems,
    setItems
  }
}